import React from 'react';
import { Space, Table, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { InvoiceCoverage, InvoiceCoverageInvoice, InvoiceCoverageLine } from '../../../types/invoiceCoverage';
import { coverageMarkerLabel, formatCoverageQuantity, formatRecognitionMonth } from '../invoiceCoverageLabels';

/**
 * WP11: coverage marker of a ZK/ZD as a Tag — „Zafakturowane częściowo (x %)”
 * or „Zafakturowane w całości”; nothing for NONE or unknown coverage.
 */
export function InvoiceCoverageTag({ coverage }: { coverage: InvoiceCoverage | null | undefined }) {
  const { t } = useTranslation('invoices');
  const label = coverageMarkerLabel(coverage, t);
  if (!label) return null;
  return <Tag color={coverage?.state === 'FULL' ? 'green' : 'geekblue'} data-testid="invoice-coverage-tag">{label}</Tag>;
}

function invoiceLabel(invoice: InvoiceCoverageInvoice, t: TFunction): string {
  const number = invoice.number || t('invoices:workflow.coverage.unnumbered');
  const details = [formatRecognitionMonth(invoice.recognitionMonth), invoice.corrected ? t('invoices:workflow.coverage.corrected') : null].filter(Boolean);
  return details.length ? `${number} (${details.join(', ')})` : number;
}

/**
 * WP11: the order's active invoices (number, recognition month) and the
 * invoiced / remaining quantity of every line. Quantities only — safe for
 * OPERATIONS and the sales rep; the backend never sends money here.
 */
export default function InvoiceCoverageSummary({ coverage }: { coverage: InvoiceCoverage | null | undefined }) {
  const { t } = useTranslation('invoices');
  if (!coverage || coverage.state === 'NONE') return null;
  return <Space direction="vertical" size={12} style={{ width: '100%' }} data-testid="invoice-coverage-summary">
    {coverage.invoices.length > 0 && <Typography.Text type="secondary" data-testid="invoice-coverage-invoices">
      {t('invoices:workflow.coverage.invoices', { invoices: coverage.invoices.map((invoice) => invoiceLabel(invoice, t)).join(', ') })}
    </Typography.Text>}
    <Table<InvoiceCoverageLine>
      rowKey="id"
      size="small"
      pagination={false}
      dataSource={coverage.lines}
      columns={[
        { title: t('invoices:workflow.coverage.line'), render: (_, line) => line.name || '—' },
        { title: t('invoices:workflow.coverage.ordered'), align: 'right', render: (_, line) => formatCoverageQuantity(line.orderedQuantity, line.unit) },
        { title: t('invoices:workflow.coverage.invoiced'), align: 'right', render: (_, line) => formatCoverageQuantity(line.invoicedQuantity, line.unit) },
        { title: t('invoices:workflow.coverage.remaining'), align: 'right', render: (_, line) => formatCoverageQuantity(line.remainingQuantity, line.unit) },
      ]}
    />
  </Space>;
}
